import h from 'hyperscript';
import { html, render } from 'lit-html';

import { requireNonNullable } from '@disy/lib/custom-error';
import { on } from '@disy/lib/event-util';
import type { ObservedAttribute } from '@disy/lib/html-element-util';
import { PLACEMENT } from '@disy/lib/position';
import type { Teardown } from '@disy/lib/teardown';
import { tearDown } from '@disy/lib/teardown';
import { createAnchorButton } from '@disy/ui/button';
import { closeAllPopups } from '@disy/ui/popup';

import { createToggletip } from 'ui/toggletip/toggletip';

import { Command } from 'cadenza/api-client/workbook-commands';
import { isFeatureAvailable } from 'cadenza/features';
import { getStore } from 'cadenza/store/cadenza-store';
import { observeStore } from 'cadenza/store/state-utils';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorkbookNotification } from 'cadenza/workbook/notifications/workbook-notifications';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import { getNotificationsForView } from 'cadenza/workbook/workbook-core-selectors.state';

import './view-notification-button.css';
import i18n from './view-notification-button.properties';

const COMPONENT_NAME = 'd-view-notification-button';

class ViewNotificationButton extends HTMLElement {
  static get observedAttributes() {
    return ['alert-type', 'view-id'];
  }

  #teardowns: Teardown[] = [];

  connectedCallback() {
    this.classList.add(COMPONENT_NAME);
    this.#render();
    this.#observeViewNotifications();
  }

  disconnectedCallback() {
    this.#teardowns = tearDown(this.#teardowns);
  }

  attributeChangedCallback(name: ObservedAttribute<typeof ViewNotificationButton>) {
    if (name === 'alert-type') {
      this.#render();
    }
  }

  #render() {
    render(
      html`${createToggletip({
        toggletipPlacement: PLACEMENT.TOP,
        toggletipContent: () => this.#createToggletipContent(),
        styleClass: ['d-alert', this.#alertTypeClass],
      })}`,
      this,
    );
  }

  #createToggletipContent() {
    return h('d-alert', { type: this.getAttribute('alert-type'), title: this.#title, role: 'menu' }, [
      h('p', [this.#message, h('br'), this.#createDetachViewAnchorButton()]),
    ]);
  }

  #createDetachViewAnchorButton() {
    if (isFeatureAvailable('COUPLE_MAP_VIEWS')) {
      const detachViewButton = createAnchorButton(i18n('detachView'), {});
      on(detachViewButton, 'click', () => {
        getStore().dispatch(executeCommand(Command.DETACH_VIEW({ viewId: this.#viewId })));
        closeAllPopups((popup) => popup.contains(detachViewButton));
      });
      return detachViewButton;
    }
  }

  #observeViewNotifications() {
    this.#teardowns.push(
      observeStore(
        getStore(),
        (state) => getNotificationsForView(state, this.#viewId),
        (notifications) => {
          this.#onNotificationsUpdate(notifications);
        },
        true,
      ),
    );
  }

  #onNotificationsUpdate(notifications: WorkbookNotification[]) {
    const errors = hasError(notifications);
    const warnings = hasWarning(notifications);
    if (errors) {
      this.setAttribute('alert-type', 'error');
    } else if (warnings) {
      this.setAttribute('alert-type', 'warning');
    }
    this.hidden = !(errors || warnings);
  }

  get #viewId() {
    return requireNonNullable(this.getAttribute('view-id'), 'ViewId not defined') as ViewId;
  }

  get #alertTypeClass() {
    return `d-alert-${this.getAttribute('alert-type') ?? 'warning'}`;
  }

  get #title() {
    return i18n(`title.<${this.getAttribute('alert-type')}>`);
  }

  get #message() {
    if (isFeatureAvailable('COUPLE_MAP_VIEWS')) {
      return i18n(`message.<${this.getAttribute('alert-type')}>`);
    } else {
      return i18n(`message.restricted.<${this.getAttribute('alert-type')}>`);
    }
  }
}

customElements.define(COMPONENT_NAME, ViewNotificationButton);
declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_NAME]: ViewNotificationButton;
  }
}

export function createViewNotificationButton(viewId: ViewId) {
  return h<ViewNotificationButton>(COMPONENT_NAME, {
    attrs: { 'alert-type': 'warning', 'view-id': viewId },
    hidden: true,
  });
}

function hasError(newNotifications: WorkbookNotification[]) {
  return newNotifications.some((notification) => notification.level === 'FATAL' || notification.level === 'ERROR');
}

function hasWarning(newNotifications: WorkbookNotification[]) {
  return newNotifications.some((notification) => notification.level === 'WARNING');
}
